/**
 * What the opened comanda is pointing at, resolved fresh on every render.
 *
 * The modal holds an identifier, not a copy of the order. Everything the board
 * learns — a dish added, a wave called, a status advanced — the modal learns in
 * the same tick, and an action taken inside it shows its result without the
 * cook closing anything.
 *
 * The other half is knowing when to stop. A comanda completed or cancelled
 * elsewhere must stop being presented as live: a kitchen that keeps reading a
 * dead ticket cooks it twice.
 */
import type { TableGroup } from "@shared/kds/table-board";
import { visibleItemsFor } from "./order-card";

export type ComandaTarget =
  /** A table. There is one comanda per table; the key is the board's group key. */
  | { kind: "table"; key: string }
  /** One station's share of one order, as the station columns show it. */
  | { kind: "station-order"; orderId: string; stationId?: string };

export type ComandaGoneReason =
  /** The kitchen is done with it, or the till cancelled it. */
  | "finished"
  /** It is not on the board any more. */
  | "not-found"
  /** It is there, but nothing is left for this station to cook. */
  | "empty";

export type ComandaResolution =
  | { kind: "table"; group: TableGroup }
  | { kind: "station-order"; order: any; items: any[] }
  | { kind: "gone"; reason: ComandaGoneReason };

/** The board's live data, exactly as the views already hold it. */
export interface ComandaSource {
  groups?: TableGroup[];
  orders?: any[];
}

const FINISHED_STATUSES = new Set(["COMPLETED", "CANCELLED"]);

/**
 * Whether the kitchen still has business with this order.
 *
 * Paid counts as finished here. This screen is the kitchen's, not the till's:
 * an order that has been paid for has left, whatever else is true of it.
 */
export function isOrderStillLive(order: any): boolean {
  if (!order) return false;
  if (FINISHED_STATUSES.has(order.status)) return false;
  if (order.paymentStatus === "PAID") return false;
  return true;
}

export function resolveComandaTarget(
  target: ComandaTarget,
  source: ComandaSource,
): ComandaResolution {
  if (target.kind === "table") {
    const group = (source.groups ?? []).find((g) => g.key === target.key);
    // The board drops a group as soon as nothing on it is left to cook, so
    // "not on the board" is the whole test here.
    return group ? { kind: "table", group } : { kind: "gone", reason: "not-found" };
  }

  const order = (source.orders ?? []).find((o) => o?.id === target.orderId);
  if (!order) return { kind: "gone", reason: "not-found" };
  if (!isOrderStillLive(order)) return { kind: "gone", reason: "finished" };

  const items = visibleItemsFor(order, target.stationId);
  if (items.length === 0) return { kind: "gone", reason: "empty" };

  return { kind: "station-order", order, items };
}
